/**
 * 🔐 Auth0 Configuration for Coach Cedar
 * Professional OAuth integration with Google Sign-In
 * Cross-platform support for web and native
 *
 * Auth0 Dashboard (Required for logout on native):
 * Application → Settings → Allowed Logout URLs must include the exact returnTo URL
 * for each platform (e.g. iOS callback below). Otherwise logout shows "Oops!, something went wrong".
 */

import { Platform } from 'react-native';

// Get redirect URI based on platform and environment
const getRedirectUri = () => {
  if (Platform.OS === 'web') {
    // Check if running locally (development)
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const port = window.location.port;
      const protocol = window.location.protocol;
      
      // Local development detection
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//${hostname}${port ? ':' + port : ''}/auth/callback`;
      }
      
      // Staging environment
      if (hostname === 'app-staging.coachcedar.com') {
        return 'https://app-staging.coachcedar.com/auth/callback';
      }
    }
    
    // Production default
    return 'https://app.coachcedar.com/auth/callback';
  } else if (Platform.OS === 'ios') {
    // react-native-auth0 SDK appends .auth0 to bundle ID by default
    // Using custom domain: login.coachcedar.com
    return 'com.coachcedar.app.auth0://login.coachcedar.com/ios/com.coachcedar.app/callback';
  } else if (Platform.OS === 'android') {
    // react-native-auth0 SDK appends .auth0 to bundle ID by default
    // Using custom domain: login.coachcedar.com
    return 'com.coachcedar.app.auth0://login.coachcedar.com/android/com.coachcedar.app/callback';
  }
  return 'https://app.coachcedar.com/auth/callback';
};

export const auth0Config = {
  // Your Auth0 credentials (using custom domain)
  domain: 'login.coachcedar.com',
  clientId: 'agMKpwVd5TWnjqua1ELYgX3dbxscEfZI',
  
  // Platform-specific redirect configuration
  redirectUri: getRedirectUri(),
  
  // Auth0 configuration - API audience for refresh tokens
  // Note: audience stays as production API for JWT validation consistency
  audience: 'https://api.coachcedar.com',
  scope: 'openid profile email offline_access',
  
  // Email configuration (handled by Auth0 + SendGrid)
  emailProvider: 'sendgrid',
  emailFrom: 'noreply@coachcedar.com',
  emailFromName: 'Coach Cedar',
  
  // Coach Cedar specific settings - optimized for persistent sessions
  useRefreshTokens: true, // Enable refresh tokens for "always logged in" experience
  // Platform-specific cache location (web: localStorage, native: memory)
  cacheLocation: Platform.OS === 'web' ? ('localstorage' as const) : ('memory' as const),
  
  // Advanced settings for reliability and debugging
  httpTimeoutInSeconds: 60,
  legacySameSiteCookie: false,
  
  // Additional reliability settings for state parameter validation
  skipRedirectCallback: false,
  
  // Auth0 React SDK specific settings
  authorizationParams: {
    prompt: 'select_account' as const, // Always show account picker for Google (first time only)
    screen_hint: undefined, // Let Auth0 decide the screen
    
    // Explicit state parameter handling
    response_type: 'code' as const,
    response_mode: 'query' as const, // Ensure state comes back in query params
  },
};

// Auth0 connection names for different providers
export const auth0Connections = {
  google: 'google-oauth2',
  apple: 'apple', // Sign in with Apple
  email: 'Username-Password-Authentication',
  // Alternative connection names in case the defaults don't work
  emailAlt: 'email',
  googleAlt: 'google',
};

// Native Google Sign-In OAuth client IDs (used by @react-native-google-signin/google-signin)
// These identify the iOS app + Android app to Google. Auth0's google-oauth2 connection
// has the iOS + Android client IDs in "Allowed Mobile Client IDs" so it accepts ID tokens
// minted for those audiences.
//
// webClientId is REQUIRED on Android for the SDK to request an idToken (which we exchange
// with Auth0). It's the SAME web client ID Auth0 uses internally for the google-oauth2
// connection. Without it, GoogleSignin.signIn() returns DEVELOPER_ERROR on Android.
export const googleNativeSignIn = {
  iosClientId: '788522045775-q7pr5j5pap44ihj6jmp8om9li3f3cn3n.apps.googleusercontent.com',
  // Android client ID isn't passed to GoogleSignin.configure() — the SDK derives it from
  // the app's package name + signing fingerprint at runtime. Kept here for reference.
  androidClientId: '788522045775-nuhit70ceg2tna32f22et7euhkq18r4r.apps.googleusercontent.com',
  // Auth0's web client for google-oauth2 connection. Used as the audience for the ID token
  // requested by GoogleSignin on Android (required by the SDK to issue an idToken).
  webClientId: '788522045775-skiejlpub2eah0bpl3bo71cdl2j222kd.apps.googleusercontent.com',
};
// FORCE DEPLOYMENT - Auth0 audience fix - Sat Sep 20 15:21:16 EDT 2025
